/**
 * Expected error types that should be filtered from Sentry.
 * These are user-initiated errors (like denying microphone permission)
 * that are already handled gracefully in the UI.
 */
export const EXPECTED_ERRORS = ['NotAllowedError', 'NotFoundError'] as const;
export type ExpectedErrorType = (typeof EXPECTED_ERRORS)[number];
export type ExpectedMicrophoneAcquisitionFailure = 'permission_denied' | 'device_not_found';

/**
 * Error message patterns that should be filtered from Sentry.
 * These match common expected errors that are already handled gracefully.
 */
export const IGNORED_ERROR_PATTERNS = [
  'Request failed with status code 4', // Client errors (4xx)
  'Request aborted', // Axios aborted requests
  'Network Error', // Network connectivity issues
] as const;

/**
 * Type guard to check if an error is an expected error type.
 * Can be used in local error handlers if needed.
 */
export function isExpectedError(error: unknown): error is Error & { name: ExpectedErrorType } {
  return error instanceof Error && EXPECTED_ERRORS.includes(error.name as ExpectedErrorType);
}

export function getExpectedMicrophoneAcquisitionFailure(
  error: unknown
): ExpectedMicrophoneAcquisitionFailure | undefined {
  if (!isExpectedError(error)) {
    return undefined;
  }

  return error.name === 'NotAllowedError' ? 'permission_denied' : 'device_not_found';
}

/**
 * Check if a message matches any of the ignored error patterns.
 */
export function isIgnoredErrorMessage(message: string): boolean {
  return IGNORED_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}
